<template>
  <div :class="containerVariants({ theme })">
    <h3 :class="titleVariants({ theme })">
      {{ subTitle }}
    </h3>
    <p
      v-if="lastDate && lastMessage"
      :class="['-mt-4 mb-6', theme === THEMES.RDK ? 'text-pink' : 'text-blue']"
    >
      <span v-if="new Date() < new Date(lastDate)">
        <!-- "Sista beställningsdag för garanterad leverans till julafton har nu passerat." -->
        {{ $t('delivery.lateMessage2', { date: formattedLastDate, message: lastMessage }) }}
      </span>
      <span v-else>
        {{ t('delivery.lateMessage-past-date', { message: lastMessage }) }}
      </span>
    </p>
    <div class="flex flex-col lg:flex-row lg:gap-4">
      <Input
        :label="labels[theme].firstName"
        v-model="modelRecipient.name.first"
        :style-input="inputVariants({ theme })"
        :style-error="inputErrorVariants({ theme })"
        :v$="v$?.name?.first"
        class="mb-7"
        required
      />
      <Input
        :label="labels[theme].lastName"
        v-model="modelRecipient.name.last"
        :style-input="inputVariants({ theme })"
        :style-error="inputErrorVariants({ theme })"
        :v$="v$?.name?.last"
        class="mb-7"
        required
      />
    </div>
    <DatePickerFlow
      :label="labels[theme].birthDate"
      v-model:date="modelRecipient.birthDate"
      :v$="v$?.birthDate"
      :required="v$?.birthDate?.required ? true : false"
      :style-error="inputErrorVariants({ theme })"
      :theme="theme"
      :start-year="startYear"
    />

    <p v-if="theme === THEMES.RDK" class="'text-sm lg:text-base' mb-2 mt-2 pl-1">
      {{ t('child-details.birth-date.description') }}
    </p>

    <CheckboxGroupWithOutBg
      v-if="[THEMES.DJURENS_RATT, THEMES.RDK].includes(theme)"
      :label="t('child-details.same-address')"
      v-model="modelSameAddress"
      :description="
        theme === THEMES.RDK
          ? $t('child-details.same-address.description')
          : $t('recipient-details.same-address.description')
      "
      class="my-7"
      :theme="theme"
    />
    <CheckboxGroup
      v-if="theme === THEMES.DEFAULT"
      :label="t('recipient-details.same-address')"
      v-model="modelSameAddress"
      :description="$t('recipient-details.same-address.description')"
      class="my-7"
    />
    <div class="flex flex-col lg:flex-row lg:gap-4" v-if="!sameAddress">
      <Input
        :label="labels[theme].streetAddress"
        v-model="modelRecipient.address.street"
        :style-input="inputVariants({ theme })"
        :style-error="inputErrorVariants({ theme })"
        :v$="v$?.address?.street"
        :required="v$?.address?.street?.required ? true : false"
        class="mb-7"
      />
      <Input
        :label="labels[theme].postcode"
        v-model="modelRecipient.address.postcode"
        :style-input="inputVariants({ theme })"
        :style-error="inputErrorVariants({ theme })"
        :required="v$?.address?.postcode?.required ? true : false"
        :v$="v$?.address?.postcode"
        class="mb-7"
      />
    </div>
    <Button
      v-if="siteOrg !== 'raddadjuren'"
      :title="t('button.next')"
      color="pink"
      class="mt-4 w-full lg:w-[315px]"
      @click="handleClickNext"
      :arrow-right="true"
    />
    <ButtonAct
      v-if="siteOrg === 'raddadjuren'"
      :title="$t('button.next')"
      :theme="theme"
      :isCta="true"
      :variant="'primary'"
      :iconType="'arrowRight'"
      @click="handleClickNext"
    />
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import 'dayjs/locale/sv'
import 'dayjs/locale/en'
import 'dayjs/locale/uk'
import 'dayjs/locale/es'
import type { Validation } from '@vuelidate/core'
import type { Person } from 'shared-types'
import { useVModel } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

import { Input, Button, THEMES, CheckboxGroupWithOutBg, CheckboxGroup } from 'refresh-ui'
import DatePickerFlow from './DatePickerFlow.vue'
import { cva } from 'class-variance-authority'
import ButtonAct from '../atoms/ButtonAct.vue'
import type { SiteOrg } from '../../types/env'

const { t, locale } = useI18n()

const props = withDefaults(
  defineProps<{
    recipient: Person
    sameAddress: boolean
    v$: Validation
    subTitle?: string
    theme?: THEMES
    siteOrg?: SiteOrg
    lastDate?: string
    lastMessage?: string
  }>(),
  {
    theme: THEMES.DEFAULT,
  },
)
const emits = defineEmits(['update:child', 'update:sameAddress', 'onNext'])

const modelRecipient = useVModel(props, 'recipient', emits)
const modelSameAddress = useVModel(props, 'sameAddress', emits)

const formattedLastDate = computed(() => {
  if (!props.lastDate) return ''
  return dayjs(props.lastDate).locale(locale.value).format('DD MMMM').toLocaleLowerCase()
})

const startYear = props.theme === THEMES.RDK ? dayjs().year() - 6 : 1990

const labels = {
  [THEMES.DEFAULT]: {
    firstName: t('recipient-details.firstname'),
    lastName: t('recipient-details.lastname'),
    birthDate: t('recipient-details.birth-date'),
    sameAddress: t('recipient-details.same-address'),
    streetAddress: t('recipient-details.street-address'),
    postcode: t('recipient-details.postcode'),
  },
  [THEMES.RDK]: {
    firstName: t('child-details.firstname'),
    lastName: t('child-details.lastname'),
    birthDate: t('child-details.birth-date'),
    sameAddress: t('child-details.same-address'),
    streetAddress: t('child-details.street-address'),
    postcode: t('child-details.postcode'),
  },
  [THEMES.DJURENS_RATT]: {
    firstName: t('recipient-details.firstname'),
    lastName: t('recipient-details.lastname'),
    birthDate: t('recipient-details.birth-date'),
    sameAddress: t('recipient-details.same-address'),
    streetAddress: t('recipient-details.street-address'),
    postcode: t('recipient-details.postcode'),
  },
}

const handleClickNext = () => {
  if (props.v$) {
    props.v$.$touch()
    props.v$.$validate()
    if (!props.v$.$invalid) emits('onNext')
    return
  }
  emits('onNext')
}

const containerVariants = cva('w-full', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: '',
      [THEMES.RDK]:
        'font-avenir text-purple-act-dark xl:max-w-[575px] [&_label]:text-purple-act-dark [&_label]:text-body',
      [THEMES.DJURENS_RATT]: 'xl:max-w-[575px] mb-8',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const titleVariants = cva('', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: 'font-serif text-xl leading-tight mb-6 xl:mb-12 xl:text-28 font-normal',
      [THEMES.RDK]:
        'mt-2 lg:mt-0 font-avenir text-h4 lg:text-h3 text-purple-act-dark lg:mb-14 lg:text-center lg:text-28',
      [THEMES.DJURENS_RATT]: 'mb-9 text-[20px] xl:mb-14 xl:text-center xl:text-28',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const inputVariants = cva('border w-full', {
  variants: {
    theme: {
      [THEMES.DEFAULT]:
        'border-blue-dark focus:border-blue-dark focus-visible:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)]',
      [THEMES.RDK]:
        'outline-0 rounded border-rdk-orange focus:border-rdk-orange focus-visible:border-peach-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)]',
      [THEMES.DJURENS_RATT]:
        'border-blue-dark focus:border-blue-dark focus-visible:border-blue-darkshadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)]',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const inputErrorVariants = cva('relative bg-transparent', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: '',
      [THEMES.RDK]: 'text-[16px] lg:text-[18px]',
      [THEMES.DJURENS_RATT]: '',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})
</script>
